import React, { useState, useEffect } from "react";
import axios from "axios";
import { Phone, Mail, MapPin, Clock, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { PHONE, PHONE_TEL, EMAIL, ADDRESS } from "../lib/assets";
import { useLang } from "../context/LangContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const { t } = useLang();
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    transmission: "manual",
    preferred_date: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const handler = (e) => {
      setForm((f) => ({ ...f, transmission: e.detail }));
    };
    window.addEventListener("preset-transmission", handler);
    return () => window.removeEventListener("preset-transmission", handler);
    // handler is defined inside the effect and listener attached once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/bookings`, form);
      toast.success(t.contact.success);
      setForm({
        name: "",
        phone: "",
        email: "",
        transmission: "manual",
        preferred_date: "",
        message: "",
      });
    } catch (err) {
      console.error(err);
      toast.error(t.contact.error);
    } finally {
      setSubmitting(false);
    }
  };

  const contactItems = [
    { icon: <Phone className="w-5 h-5" />, label: t.contact.info.phone, value: PHONE, href: `tel:${PHONE_TEL}` },
    { icon: <Mail className="w-5 h-5" />, label: t.contact.info.email, value: EMAIL, href: `mailto:${EMAIL}` },
    { icon: <MapPin className="w-5 h-5" />, label: t.contact.info.address, value: t.contact.info.addressValue, href: null },
    { icon: <Clock className="w-5 h-5" />, label: t.contact.info.hours, value: t.contact.info.hoursValue, href: null },
  ];

  return (
    <section id="contact" data-testid="contact-section" className="py-24 sm:py-32 bg-brand-cream relative overflow-hidden">
      <div className="absolute top-20 right-0 w-96 h-96 rounded-full bg-brand-orange/10 blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-96 h-96 rounded-full bg-brand-navy/10 blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="text-center max-w-3xl mx-auto mb-14">
          <p className="text-xs sm:text-sm uppercase tracking-[0.22em] font-bold text-brand-orange mb-4">
            {t.contact.eyebrow}
          </p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-brand-navy leading-tight tracking-tight">
            {t.contact.title}
          </h2>
          <p className="mt-5 text-base sm:text-lg text-brand-muted leading-relaxed">
            {t.contact.subtitle}
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-8 lg:gap-12">
          {/* Contact info */}
          <div className="lg:col-span-2">
            <div className="bg-brand-navy text-white rounded-3xl p-7 sm:p-9 h-full">
              <h3 className="font-display text-2xl font-bold mb-6">BIU BIU DRIVING</h3>
              <p className="text-white/80 text-sm leading-relaxed mb-8">
                {t.footer.tagline}
              </p>

              <div className="space-y-5">
                {contactItems.map((item, i) => (
                  <div key={item.label} className="flex items-start gap-4" data-testid={`contact-info-${i}`}>
                    <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-brand-orange/20 text-brand-orange flex items-center justify-center">
                      {item.icon}
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-wider text-white/60 font-semibold">{item.label}</p>
                      {item.href ? (
                        <a href={item.href} className="text-white font-medium hover:text-brand-orange transition-colors break-all">
                          {item.value}
                        </a>
                      ) : (
                        <p className="text-white font-medium">{item.value}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-3">
            <form
              onSubmit={onSubmit}
              data-testid="booking-form"
              className="bg-white rounded-3xl p-7 sm:p-9 border border-brand-border-soft shadow-sm"
            >
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label={t.contact.labels.name} required>
                  <input
                    type="text"
                    name="name"
                    value={form.name}
                    onChange={onChange}
                    required
                    minLength={2}
                    data-testid="booking-name"
                    className="w-full px-4 py-3 rounded-xl border border-brand-border-soft bg-white text-brand-navy focus:outline-none focus:ring-2 focus:ring-brand-orange focus:border-transparent transition-all"
                  />
                </Field>

                <Field label={t.contact.labels.phone} required>
                  <input
                    type="tel"
                    name="phone"
                    value={form.phone}
                    onChange={onChange}
                    required
                    data-testid="booking-phone"
                    className="w-full px-4 py-3 rounded-xl border border-brand-border-soft bg-white text-brand-navy focus:outline-none focus:ring-2 focus:ring-brand-orange focus:border-transparent transition-all"
                  />
                </Field>

                <Field label={t.contact.labels.email} required>
                  <input
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={onChange}
                    required
                    data-testid="booking-email"
                    className="w-full px-4 py-3 rounded-xl border border-brand-border-soft bg-white text-brand-navy focus:outline-none focus:ring-2 focus:ring-brand-orange focus:border-transparent transition-all"
                  />
                </Field>

                <Field label={t.contact.labels.date}>
                  <input
                    type="date"
                    name="preferred_date"
                    value={form.preferred_date}
                    onChange={onChange}
                    data-testid="booking-date"
                    className="w-full px-4 py-3 rounded-xl border border-brand-border-soft bg-white text-brand-navy focus:outline-none focus:ring-2 focus:ring-brand-orange focus:border-transparent transition-all"
                  />
                </Field>

                <div className="sm:col-span-2">
                  <Field label={t.contact.labels.transmission} required>
                    <select
                      name="transmission"
                      value={form.transmission}
                      onChange={onChange}
                      required
                      data-testid="booking-transmission"
                      className="w-full px-4 py-3 rounded-xl border border-brand-border-soft bg-white text-brand-navy focus:outline-none focus:ring-2 focus:ring-brand-orange focus:border-transparent transition-all"
                    >
                      {Object.entries(t.contact.labels.services).map(([code, label]) => (
                        <option key={code} value={code}>{label}</option>
                      ))}
                    </select>
                  </Field>
                </div>

                <div className="sm:col-span-2">
                  <Field label={t.contact.labels.message}>
                    <textarea
                      name="message"
                      value={form.message}
                      onChange={onChange}
                      rows={4}
                      data-testid="booking-message"
                      className="w-full px-4 py-3 rounded-xl border border-brand-border-soft bg-white text-brand-navy focus:outline-none focus:ring-2 focus:ring-brand-orange focus:border-transparent transition-all resize-none"
                    />
                  </Field>
                </div>
              </div>

              <button
                type="submit"
                disabled={submitting}
                data-testid="booking-submit-btn"
                className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-brand-orange hover:bg-brand-orange-deep disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold px-7 py-4 rounded-full transition-all hover:-translate-y-0.5 shadow-md hover:shadow-xl"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    {t.contact.labels.submitting}
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    {t.contact.labels.submit}
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

const Field = ({ label, required, children }) => (
  <label className="block">
    <span className="block text-xs uppercase tracking-wider font-bold text-brand-navy mb-2">
      {label}{required && <span className="text-brand-orange ml-1">*</span>}
    </span>
    {children}
  </label>
);

export default Contact;
